import { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { api, imageUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { MapPin, Phone, Mail, ExternalLink, ArrowLeft, Store, ShieldCheck } from "lucide-react";
import VerifiedBadge from "@/components/VerifiedBadge";
import ReportButton from "@/components/ReportButton";
import SaveButton from "@/components/SaveButton";
import MessageButton from "@/components/MessageButton";
import AdminRemoveButton from "@/components/AdminRemoveButton";

export default function BusinessDetail() {
  const { slug } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [biz, setBiz] = useState(null);

  const load = () => api.get(`/businesses/${slug}`).then((r) => setBiz(r.data)).catch(() => setBiz(false));
  useEffect(() => { load(); }, [slug]);

  if (biz === false) return <div className="max-w-4xl mx-auto p-10 text-center text-[#5C5C5C]">Business not found.</div>;
  if (!biz) return <div className="max-w-4xl mx-auto p-10 text-center text-[#5C5C5C]">Loading…</div>;

  const claim = async () => {
    try {
      await api.post(`/businesses/${slug}/claim`);
      toast.success("Claimed — this page is yours");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not claim"); }
  };

  const isOwner = user && biz.owner_id === user.id;
  const unclaimed = !biz.owner_id;

  return (
    <div>
      <section className="bg-[#1E3A2F] text-[#F7F4EB] relative overflow-hidden">
        <div className="absolute inset-0 abm-kente opacity-[0.06]" />
        <div className="max-w-6xl mx-auto px-5 sm:px-8 py-12 relative">
          <button onClick={() => nav(-1)} className="inline-flex items-center gap-2 text-sm text-[#DDA74A] mb-4"><ArrowLeft className="w-4 h-4" /> Back</button>
          <div className="flex items-start gap-5 flex-wrap">
            <div className="w-20 h-20 rounded-2xl bg-white/10 grid place-items-center overflow-hidden">
              {biz.logo_path ? <img src={imageUrl(biz.logo_path)} alt="" className="w-full h-full object-cover" /> : <Store className="w-8 h-8 text-[#DDA74A]" />}
            </div>
            <div className="flex-1 min-w-[240px]">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="abm-chip !bg-[#DDA74A]/15 !text-[#DDA74A] !border-[#DDA74A]/40">{biz.category}</span>
                {biz.verified && <VerifiedBadge tier={biz.verification_tier} size="sm" />}
                {unclaimed && <span className="text-xs text-[#DDA74A]">Unclaimed</span>}
              </div>
              <h1 className="font-display text-4xl sm:text-5xl mt-2 leading-tight">{biz.name}</h1>
              {biz.tagline && <p className="text-[#A3B3AA] mt-2">{biz.tagline}</p>}
              <div className="mt-3 text-sm text-[#A3B3AA] inline-flex items-center gap-1"><MapPin className="w-4 h-4" /> {biz.location}</div>
            </div>
            <div className="flex flex-col gap-2 min-w-[220px]">
              {!isOwner && unclaimed && user && (
                <Button data-testid="biz-claim" onClick={claim} className="abm-btn-primary rounded-full"><ShieldCheck className="w-4 h-4 mr-1" /> Claim this page</Button>
              )}
              {!isOwner && biz.owner_id && (
                <MessageButton
                  recipientId={biz.owner_id}
                  recipientName={biz.name || "Owner"}
                  targetType="business"
                  targetId={biz.slug}
                  targetTitle={biz.name}
                  size="sm"
                />
              )}
              {!isOwner && <SaveButton type="business" id={biz.slug} />}
              <AdminRemoveButton type="business" id={biz.slug} title={biz.name} redirectTo="/businesses" />
              {isOwner && (
                <div className="inline-flex items-center gap-1 self-start px-3 py-1 rounded-full bg-[#DDA74A]/15 text-[#DDA74A] border border-[#DDA74A]/40 text-xs font-medium" data-testid="biz-owned">
                  <ShieldCheck className="w-3 h-3" /> You own this page
                </div>
              )}
              <div className="rounded-2xl bg-white/10 border border-white/10 p-4 text-center">
                <div className="font-display text-3xl text-[#DDA74A]">{biz.recommendation_count}</div>
                <div className="text-xs uppercase tracking-widest text-[#A3B3AA] mt-1">Community recs</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-5 sm:px-8 py-10 grid lg:grid-cols-[1.4fr_0.6fr] gap-10">
        <div>
          <h2 className="font-display text-2xl text-[#1E3A2F]">About</h2>
          <p className="mt-3 whitespace-pre-wrap text-[#1A1A1A] leading-relaxed">{biz.description}</p>

          <h2 className="font-display text-2xl text-[#1E3A2F] mt-10">Recommendations from the community</h2>
          <div className="mt-4 space-y-3">
            {(biz.recommendations || []).length === 0 ? (
              <div className="rounded-xl border border-dashed border-[#E2DED0] p-8 text-center text-[#5C5C5C]">
                No public recommendations yet. Ask happy customers to shout you out from a matching need.
              </div>
            ) : (biz.recommendations || []).map((r) => (
              <div key={r.id} className="rounded-xl border border-[#E2DED0] bg-white p-4">
                <div className="flex items-baseline justify-between gap-2">
                  <div className="font-medium text-[#1E3A2F]">{r.recommender_name}</div>
                  <Link to={`/needs/${r.need_id}`} className="text-xs text-[#C05A3D]">on a request →</Link>
                </div>
                {r.note && <p className="mt-2 text-sm text-[#1A1A1A]">{r.note}</p>}
              </div>
            ))}
          </div>
        </div>

        <aside className="lg:sticky lg:top-24 h-fit rounded-2xl bg-white border border-[#E2DED0] p-6 space-y-3">
          <div className="text-xs uppercase tracking-widest text-[#5C5C5C]">Contact</div>
          {biz.phone && <ContactRow icon={<Phone className="w-4 h-4" />} label={biz.phone} href={`tel:${biz.phone}`} />}
          {biz.email && <ContactRow icon={<Mail className="w-4 h-4" />} label={biz.email} href={`mailto:${biz.email}`} />}
          {biz.website && (
            <ContactRow icon={<ExternalLink className="w-4 h-4" />} label={biz.website.replace(/^https?:\/\//, "")}
              href={biz.website.startsWith("http") ? biz.website : `https://${biz.website}`} target="_blank" />
          )}
          {biz.address && <div className="text-sm text-[#5C5C5C]">{biz.address}</div>}
          {!isOwner && (
            <div className="pt-3 border-t border-[#E2DED0] flex justify-end">
              <ReportButton targetType="business" targetId={biz.slug} targetTitle={biz.name} />
            </div>
          )}
        </aside>
      </section>
    </div>
  );
}

function ContactRow({ icon, label, href, target }) {
  return (
    <a href={href} target={target} rel="noreferrer" className="flex items-center gap-2 text-sm text-[#1E3A2F] hover:text-[#C05A3D]">
      {icon} {label}
    </a>
  );
}
